import React, { useCallback } from 'react';
import ActionButton from '@westwing/ui-kit/ActionButton';
import useForm from '@westwing/ui-kit/Tools/hooks/useForm';
import FloatingLabelInput, { Hint } from '@westwing/ui-kit/FloatingLabelInput';
import { VALIDATE_HOOK } from '../loginAndRegistration.types';
import {
    StyledHeader,
    StyledModalTitle,
    StyledForm,
    StyledFieldset,
    StyledModalFormActions,
    StyledModalParagraph,
    StyledModalContent,
} from '../loginAndRegistration.style';
import resetPasswordSchema from './resetPassword.schema';
import useTranslate from 'Hooks/useTranslate';

type ResetPasswordFormValues = {
    password: string;
    confirmPassword: string;
};
export type ResetPasswordFormProps = {
    email: string;
    onSubmit: (values: ResetPasswordFormValues) => void;
};

const ResetPasswordForm = ({ email, onSubmit: onFormSubmit }: ResetPasswordFormProps): JSX.Element => {
    const { t, tPlain } = useTranslate();

    const title = t('Change password');
    const description = tPlain('Create new password', [email]);
    const passwordLabel = tPlain('New password nt');
    const confirmLabel = tPlain('Password confirmation');
    const submitButtonLabel = t('Save Password');

    const schema = resetPasswordSchema(tPlain);

    const { values, errors, onBlur, onSubmit, onChange, isValid, isValidated, touched } =
        useForm<ResetPasswordFormValues>({
            schema,
            onSubmitValid: onFormSubmit,
            initialValues: {
                password: '',
                confirmPassword: '',
            },
            validateOn: [VALIDATE_HOOK.BLUR, VALIDATE_HOOK.SUBMIT],
        });

    const isConfirmValid = useCallback(() => {
        const passwordValidated = touched.password || isValidated('password');
        const passwordValid = passwordValidated && !errors?.password;
        const confirmPasswordValidated = touched.confirmPassword || isValidated('confirmPassword');
        const confirmPasswordValid = confirmPasswordValidated && !errors?.confirmPassword;

        if (!confirmPasswordValidated) {
            return undefined;
        }

        if (passwordValid && confirmPasswordValid) {
            return true;
        }

        if (!confirmPasswordValid) {
            return false;
        }

        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errors, touched]);

    return (
        <div>
            <StyledHeader>
                <StyledModalTitle>{title}</StyledModalTitle>
            </StyledHeader>
            <StyledModalContent>
                <StyledForm as="form" onSubmit={onSubmit} noValidate>
                    <StyledFieldset>
                        <StyledModalParagraph>{description}</StyledModalParagraph>
                        <FloatingLabelInput
                            label={passwordLabel}
                            type="password"
                            name="password"
                            autoComplete="new-password"
                            value={values.password}
                            onChange={onChange}
                            onBlur={onBlur}
                            isValid={isValid('password')}
                            hint={
                                isValidated('password') &&
                                !isValid('password') && <Hint.Error as="output">{errors.password[0]}</Hint.Error>
                            }
                        />

                        <FloatingLabelInput
                            label={confirmLabel}
                            value={values.confirmPassword}
                            type="password"
                            name="confirmPassword"
                            onChange={onChange}
                            onBlur={onBlur}
                            isValid={isConfirmValid()}
                            autoComplete="new-password"
                            hint={
                                isConfirmValid() === false && (
                                    <Hint.Error as="output">{errors.confirmPassword[0]}</Hint.Error>
                                )
                            }
                        />
                        <StyledModalFormActions>
                            <ActionButton kind="primary" type="submit">
                                {submitButtonLabel}
                            </ActionButton>
                        </StyledModalFormActions>
                    </StyledFieldset>
                </StyledForm>
            </StyledModalContent>
        </div>
    );
};
export default ResetPasswordForm;
